<template>
  <div ref="componentEl" :class="$style.orderSplitSummary">
    <ul :class="['ulReset', $style.infos]">
      <li
        v-for="(info, key) in infos"
        :key="key"
        :class="$style.info"
      >
        <template v-if="!info.description">
          <span :class="$style.info">
            <span v-text="info.name" />
            <span :class="$style.infoValue" v-text="info.value" />
          </span>
        </template>
        <lv-tooltip
          v-else
          styling="primary-version2"
          is-false-tooltip
          :options="{placement: info.placement}"
        >
          <template #tooltip>
            <div data-popover-item-wrapper>
              <span :class="$style.info" v-html="info.description" />
            </div>
          </template>
          <template #reference>
            <span :class="$style.info">
              <lv-icon name="education-16" :size="16" :class="$style.infoIcon" />
              <span v-text="`${info.name}:`" />
              <span :class="$style.infoValue" v-text="info.value" />
            </span>
          </template>
        </lv-tooltip>
      </li>
    </ul>
    <div :class="[$style.actions, { [$style.actionsIsFloating]: isFloating }]">
      <div
        :class="[
          $style.notification,
          {
            awSrOnlyAbs: isFloating,
          },
        ]"
        aria-live="assertive"
      >
        <aw-notification-item
          v-if="notificationTexts?.text?.subtitle"
          index="0"
          is-margin-less
          :notification="notificationTexts"
        />
      </div>
      <div
        :class="[
          $style.priceSum,
          {
            [$style.priceSumFloating]: isFloating,
          },
        ]"
        v-html="$awt(
          'aw.basket_split_summary.price_sum',
          {
            priceSum,
          },
        )"
      />
      <div
        :class="{
          [$style.goToCheckoutBtnWrapFloating]: isFloating,
        }"
      >
        <lv-button
          v-bind="navigateToCheckoutBtnAttrs.attrs"
          :styling="isFloating
            ? 'small-primary-dark'
            : 'primary-dark'
          "
          shrink
          :class="{
            [$style.goToCheckoutBtnFloating]: isFloating,
          }"
        >
          <span :class="$style.goToCheckoutBtnText">
            <span v-text="navigateToCheckoutBtnAttrs.text" />
            <lv-icon name="arrow-right-24" :size="16" />
          </span>
        </lv-button>
      </div>
    </div>
    <aw-info-modal v-bind="priceChangedModal.attrs">
      <template #actionButtons>
        <lv-button styling="primary-dark" shrink @click="priceChangedButtonOkClick">
          {{ priceChangedModal.buttonText }}
        </lv-button>
      </template>
    </aw-info-modal>
  </div>
</template>

<script setup>
  import { useCssModule, markRaw, defineAsyncComponent, ref, watch, onMounted, onUnmounted } from 'vue';
  import { navigateTo, useNuxtApp } from 'nuxt/app';
  import { camelToSnakeCase } from '~~/common/utils';
  import { Navigation, ID_CHECKOUT_TOP } from '~~/common/config/Navigation';
  import { useOrderStore } from '~~/common/stores/order';
  import { useDeliveryStore } from '~~/shop/app/stores/delivery';
  import { useUserStore } from '~~/common/stores/user';
  import { useModalStore } from '~~/common/stores/modal.js';
  import { openLoginModal } from '~~/common/composables/useLoginModal';
  import { useOrderSplit } from '~~/common/composables/useOrderSplit';
  import { LvButton, LvIcon, LvTooltip } from '~~/common/components/loginet-vue-shop/index';
  import AwGuestCheckoutWelcomeModal from '~~/shop/app/components/AwGuestCheckoutWelcomeModal.vue';
  import AwInfoModal from '~~/common/components/Common/Modals/AwInfoModal';
  import { M_INFO } from '~~/common/config/Modal';
  import { PM_ONLINE_CREDIT_CARD } from '~~/common/config/PaymentMethod';

  const AwNotificationItem = defineAsyncComponent(() => import('~~/common/components/Common/AwNotificationItem.vue'));

  const props = defineProps({
    checkoutType: {
      type: String,
      required: true,
      default: '',
    },
    isFloating: {
      type: Boolean,
      required: true,
      default: false,
    },
  });

  const $style = useCssModule();

  const {
    $price,
    $awt,
    $localePath,
    $awMergedConfig,
  } = useNuxtApp();
  const price = (gross, currency) => $price({ gross }, currency);

  const {
    getSplitByCheckoutType,
    editedOrderId,
    editedOriginalOrder,
  } = useOrderSplit();

  const split = computed(() => getSplitByCheckoutType(props.checkoutType));

  const {
    hideConfirmModal,
    showConfirmModalWithCallback,
    showInfoModal,
    hideInfoModal,
  } = useModalStore();

  const priceSum = computed(() => {
    const sum = split.value?.summary?.sum || {};
    const {
      grossDiscounted,
      currency,
    } = sum;
    return price(grossDiscounted, currency);
  });

  const componentEl = ref(null);

  const isEcomUser = computed(() => useUserStore().isEcomUser);

  const notificationTexts = computed(() => {
    const warning = Symbol('warning');
    const warningLevel = new Set(['cartValueLimits', 'containsMixedCreditable', warning]);
    return ([
      ...useOrderStore().getStaticInfosByCheckoutType[props.checkoutType],
      [
        warning,
        (!useDeliveryStore().setup?.id
          ? $awt('aw.basket_split_summary.notification.delivery_is_not_selected')
          : ''
        ),
      ],
    ]
      .reduce((acc, [k, subtitle], idx, { length: arrayLength }) => {
        if (acc.type === 'info' && subtitle && warningLevel.has(k)) {
          acc.type = 'warning';
          acc.iconName = 'maintenence-16';
        }
        if (subtitle) {
          acc.text.subtitle += `<span>${subtitle}</span>`;
        }
        if (idx === (arrayLength - 1) && acc.text.subtitle) {
          acc.text.subtitle = `<div class="${$style.subTitle}">${acc.text.subtitle}</div>`;
        }
        return acc;
      }, {
        type: 'info',
        iconName: 'education-16',
        manualClose: false,
        text: {
          subtitle: '',
        },
      })
    );
  });

  const infos = computed(() => {
    return [...(function * () {
      const splitSummary = split.value?.summary;
      if (!splitSummary) {
        return;
      }
      const infoTranslation = (tokenBase) => {
        return {
          name: $awt(`${tokenBase}.text`),
          description: $awt(`${tokenBase}.description`).replace(/^(\s*|-)$/, ''),
        };
      };
      const {
        itemsSum,
        fees,
        discounts,
      } = splitSummary;
      if (itemsSum?.gross) {
        const {
          grossDiscounted,
          currency,
        } = itemsSum;
        yield {
          ...infoTranslation('aw.basket_split_summary.summary.items_sum'),
          value: price(grossDiscounted, currency),
        };
      }
      if (fees) {
        for (const priceKey of [
          'weightSurcharge',
          'extraPalettSurcharge',
          'packaging',
          'warranty',
        ]) {
          if (fees[priceKey]?.gross) {
            const {
              gross,
              currency,
            } = fees[priceKey];
            yield {
              ...infoTranslation(`aw.basket_split_summary.summary.${camelToSnakeCase(priceKey)}`),
              value: price(gross, currency),
            };
          }
        }
        const { delivery } = fees;
        if (delivery?.sum?.gross) {
          const {
            gross,
            currency,
          } = delivery.sum;
          yield {
            ...infoTranslation('aw.basket_split_summary.summary.delivery'),
            value: price(gross, currency),
          };
        }
      }
      if (discounts?.sum?.gross) {
        const {
          gross,
          currency,
        } = discounts.sum;
        yield {
          ...infoTranslation('aw.basket_split_summary.summary.discounts'),
          value: price(-gross, currency),
        };
      }
    }())].map((e, idx, { length }) => {
      const lHalf = Math.ceil(length / 2) - 1;
      const placement = (idx < lHalf) ? 'top-start' : ((idx === lHalf) ? 'top' : 'top-end');
      return {
        ...e,
        placement,
      };
    });
  });

  const editedOriginalOrderSumPrice = computed(() => editedOriginalOrder.value?.sumPrice?.grossDiscounted);
  const currentOrderSumPrice = computed(() => split.value?.summary?.sum?.grossDiscounted);

  const isIncreasePrice = computed(() => {
    return editedOriginalOrderSumPrice.value > currentOrderSumPrice.value;
  });

  const priceChangedModalType = ref(M_INFO + 'price_changed');

  const priceChangedModal = computed(() => {
    const textIdx = isIncreasePrice.value ? '1' : '2';

    return {
      buttonText: $awt(`aw.basket.changed_basket_warning_modal_${textIdx}.ok_btn`),
      attrs: {
        title: $awt(`aw.basket.changed_basket_warning_modal_${textIdx}.title`),
        message: $awt(`aw.basket.changed_basket_warning_modal_${textIdx}.text`),
        type: priceChangedModalType.value,
        'onClose-modal': () => {
          hideInfoModal({ modalType: priceChangedModalType.value });
        },
      },
    };
  });

  const checkoutUrl = computed(() => {
    return $localePath(
      `/shop/checkout/${props.checkoutType?.toLowerCase()}#${Navigation[ID_CHECKOUT_TOP]}`,
    );
  });

  const navigateToCheckoutBtnAttrs = computed(() => {
    const isCheckoutAccessibleImmediately = isEcomUser.value && !editedOrderId.value;

    return {
      text: $awt(getToken()),
      attrs: {
        href: (isCheckoutAccessibleImmediately
          ? checkoutUrl.value
          : 'javascript:void(0)'
        ),
        onClick: onNavigateToCheckout,
      },
    };

    function getToken () {
      if (isEcomUser.value) {
        return 'aw.basket_split_summary.checkout_btn.go_to_checkout_logged_in';
      } else if ($awMergedConfig.enabledGuestCheckouts.includes(props.checkoutType)) {
        return 'aw.basket_split_summary.checkout_btn.go_to_checkout_as_guest';
      } else {
        return 'aw.basket_split_summary.checkout_btn.please_login';
      }
    }

    function onNavigateToCheckout ($event) {
      $event.preventDefault();
      if (isCheckoutAccessibleImmediately) {
        navigateTo(checkoutUrl.value);
      } else if (editedOrderId.value) {
        const isCreditCardPayment = editedOriginalOrder?.value?.checkoutData?.payment?.type === PM_ONLINE_CREDIT_CARD;
        if (isCreditCardPayment && editedOriginalOrderSumPrice.value !== currentOrderSumPrice.value) {
          showInfoModal({ modalType: priceChangedModalType.value });
        } else {
          navigateTo(checkoutUrl.value);
        }
      } else if ($awMergedConfig.enabledGuestCheckouts.includes(props.checkoutType)) {
        showConfirmModalWithCallback({
          description: 'navigateToCheckout',
          onConfirmClose () {
          },
          confirmModalConfig: {
            closable: true,
            data: {
              confirmModalSize: 'md',
              confirmTitle: $awt('aw.guest_checkout_welcome.heading'),
              confirmOkButton: $awt('aw.guest_checkout_welcome.continue_logged_in').replace(/\s/g, '\xA0'),
              confirmOkButtonStyle: 'primary-dark',
              confirmOkButtonAttrs: {
                type: null,
                href: 'javascript:void(0)',
                onClick () {
                  hideConfirmModal();
                  openLoginModal();
                },
              },
              confirmCancelButton: $awt('aw.guest_checkout_welcome.continue_as_guest').replace(/\s/g, '\xA0'),
              confirmCancelButtonStyle: 'secondary-dark',
              confirmCancelButtonAttrs: {
                type: null,
                href: 'javascript:void(0)',
                onClick () {
                  hideConfirmModal();
                  navigateTo(checkoutUrl.value);
                },
              },
              confirmComponent: markRaw(AwGuestCheckoutWelcomeModal),
            },
          },
        });
      } else {
        openLoginModal();
      }
    }
  });
  let resizeObserver;
  onMounted(() => {
    resizeObserver = new ResizeObserver((entries) => {
      const lastEntry = entries.slice(-1)[0];
      onResize(lastEntry.target, lastEntry.contentRect.width);
    });
    watch(
      componentEl,
      (newVal, oldVal) => {
        if (oldVal) {
          resizeObserver.unobserve(oldVal);
        }
        if (newVal) {
          resizeObserver.observe(newVal);
          onResize(newVal, newVal.clientWidth);
        }
      },
      {
        immediate: true,
      },
    );

    function onResize (el, floatingWidth) {
      if (el) {
        el.style.setProperty(
          '--awOrderSplitSummaryFloatingWidth',
          `${floatingWidth.toFixed(0)}px`,
        );
      }
    }
  });
  onUnmounted(() => {
    resizeObserver?.disconnect();
  });

  function priceChangedButtonOkClick () {
    hideInfoModal({ modalType: priceChangedModalType.value });
    navigateTo(checkoutUrl.value);
  }
</script>

<style module lang="scss" rel="stylesheet/scss">

.orderSplitSummary {
  display: flex;
  flex-direction: column;
  gap: 12px;

  @include desktop-medium(min) {
    gap: 24px;
  }
}

.infos {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px 24px;
}

.info {
  @include font($font-weight-normal-v2, 10px, 16px);
  display: flex;
  gap: 4px;

  @include desktop-medium(min) {
    @include font(null, 12px, null);
  }
}

.infoValue {
  @include font($font-weight-bold-v2, null, null);
}

.infoIcon {
  color: $color-plum-500;
}

.actions {
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 12px;
  border-radius: 16px;
  background: $color-background-1;

  @include tablet(min) {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
  }

  &:not(&IsFloating) {
    gap: 12px 20px;

    @include desktop-medium(min) {
      flex-wrap: nowrap;
      padding: 20px;
      gap: 12px 40px;
    }
  }

  &IsFloating {
    position: fixed;
    bottom: 0;
    width: calc(var(--awOrderSplitSummaryFloatingWidth) + 2 * var(--awBasketTabSidePadding));
    margin: 0 calc(-1 * var(--awBasketTabSidePadding));
    border-top: 1px dashed $color-dash-separator;
    border-radius: 0;
    background: $color-white;
    gap: 8px 20px;
  }
}

.notification {
  align-self: stretch;
  flex-grow: 1;

  &:empty {
    display: none;
  }

  @include desktop-small(max) {
    flex-basis: 100%;
  }
}

.priceSum:not(.priceSumFloating) {
  @include font($font-weight-bold-v2, 18px, 24px);

  @include desktop-medium(min) {
    @include font(null, 22px, 28px);
  }
}

.priceSumFloating {
  @include font($font-weight-bold-v2, 16px, 20px);

  @include tablet(min) {
    @include font(null, 18px, 24px);
  }
}

.goToCheckoutBtnWrapFloating,
.goToCheckoutBtnFloating {
  @include mobile(max) {
    width: 100%;
    max-width: 100%;
  }
}

.goToCheckoutBtnText {
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  gap: 12px;
}

.subTitle {
  @include tablet(max) {
    display: inline;

    > :not(:last-child) {
      margin-right: 12px;
    }
  }

  @include desktop-small(min) {
    display: flex;
    flex-direction: column;
  }
}
</style>
